import {
  FiHome,
  FiEdit3,
  FiBriefcase,
  FiCompass,
  FiHeart,
  FiFolder,
  FiUsers,
  FiUser,
  FiFileText,
  FiMail,
  FiHelpCircle,
  FiKey,
  FiGift,
  FiClipboard,
} from "react-icons/fi";

const createItem = (item) => ({
  ...item,
  activePrefixes: item.activePrefixes ?? [item.to],
});

export const CUSTOMER_PANEL_HOME = "/customer/workspace";

const customerSidebarMenu = [
  {
    id: "dashboard",
    title: "Dashboard",
    items: [
      {
        to: "/customer/workspace",
        label: "Overview",
        icon: FiHome,
        activePrefixes: ["/customer/workspace", "/customer/dashboard"],
      },
    ],
  },
  {
    id: "shared",
    title: "Shared",
    items: [
      {
        to: "/customer/posts",
        label: "Shared Posts",
        icon: FiEdit3,
        activePrefixes: ["/customer/posts"],
      },
      {
        to: "/customer/properties",
        label: "Shared Properties",
        icon: FiBriefcase,
        activePrefixes: ["/customer/properties"],
      },
      {
        to: "/customer/shared-leads",
        label: "Shared Leads",
        icon: FiCompass,
        activePrefixes: ["/customer/shared-leads"],
      },
      {
        to: "/customer/shared-leases",
        label: "Shared Leases",
        icon: FiKey,
        activePrefixes: ["/customer/shared-leases"],
      },
    ],
  },
  {
    id: "myspace",
    title: "My Space",
    items: [
      {
        to: "/customer/my-properties",
        label: "My Properties",
        icon: FiFolder,
        activePrefixes: ["/customer/my-properties"],
      },
      {
        to: "/customer/my-posts",
        label: "My Posts",
        icon: FiFileText,
        activePrefixes: ["/customer/my-posts"],
      },
      {
        to: "/customer/leads",
        label: "My Leads",
        icon: FiClipboard,
        activePrefixes: ["/customer/leads"],
      },
      {
        to: "/customer/leases",
        label: "My Leases",
        icon: FiKey,
        activePrefixes: ["/customer/leases"],
      },
      {
        to: "/customer/rewards",
        label: "Rewards",
        icon: FiGift,
        activePrefixes: ["/customer/rewards"],
      },
      {
        to: "/customer/saved",
        label: "Following",
        icon: FiHeart,
        activePrefixes: ["/customer/saved", "/customer/following"],
      },
      {
        to: "/customer/shared-with-me",
        label: "Shared with Me",
        icon: FiMail,
        activePrefixes: ["/customer/shared-with-me"],
      },
    ],
  },
  {
    id: "people",
    title: "People",
    items: [
      {
        to: "/customer/friends",
        label: "Friends",
        icon: FiUsers,
        activePrefixes: ["/customer/friends"],
      },
      {
        to: "/customer/agents",
        label: "Agents",
        icon: FiUser,
        activePrefixes: ["/customer/agents"],
      },
    ],
  },
].map((section) => ({
  ...section,
  items: section.items.map(createItem),
}));

export const customerSidebarSections = customerSidebarMenu;

export const customerSidebarFooter = [
  createItem({
    to: "/customer/help",
    label: "Help Center",
    icon: FiHelpCircle,
  }),
];

// Menu per normalised role. Unknown/loading role -> empty (no flash of a wrong menu).
const SIDEBAR_BY_ROLE = {
  CUSTOMER: customerSidebarMenu,
};

export const normalizeRole = (role) =>
  typeof role === "string" ? role.trim().toUpperCase() : "";

export const getCustomerSidebar = (role) =>
  SIDEBAR_BY_ROLE[normalizeRole(role)] ?? [];

// Pages that are reachable but are not sidebar items (hub tiles, create forms, ...)
const EXTRA_PAGES = [
  { prefix: "/customer/services", label: "Home Services" },
  { prefix: "/customer/bazaar", label: "Local Bazaar" },
  { prefix: "/customer/reminders", label: "Visit Reminders" },
  { prefix: "/customer/healthians", label: "Care Leads" },
  { prefix: "/customer/interior-design", label: "Interior Design" },
  { prefix: "/customer/communities", label: "Communities" },
  { prefix: "/customer/promotions", label: "Promotions" },
  { prefix: "/customer/myspace", label: "MySpace" },
  { prefix: "/customer/invitations", label: "Invitations" },
  { prefix: "/customer/brand-ambassador", label: "Brand Ambassador" },
  { prefix: "/customer/profile", label: "Profile" },
  { prefix: "/customer/help", label: "Help Center" },
  { prefix: "/notification", label: "Notifications" },
  { prefix: "/property/add-property", label: "Create Property" },
  { prefix: "/add-property", label: "Create Property" },
  { prefix: "/post/add-post", label: "Create Post" },
];

const startsWithPrefix = (pathname, prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`);

// Longest matching known page for a pathname (sidebar items first, then the extra pages)
const findPage = (pathname) => {
  const candidates = [
    ...customerSidebarSections.flatMap((s) => s.items).flatMap((item) =>
      item.activePrefixes.map((prefix) => ({ prefix, label: item.label, to: item.to }))
    ),
    ...EXTRA_PAGES.map((page) => ({ ...page, to: page.prefix })),
  ].filter((c) => startsWithPrefix(pathname, c.prefix));
  return candidates.sort((a, b) => b.prefix.length - a.prefix.length)[0];
};

export const isCustomerRoot = (pathname) =>
  pathname === "/customer" || pathname === "/customer/" || pathname === CUSTOMER_PANEL_HOME;

export const getCustomerPageTitle = (pathname) => findPage(pathname)?.label ?? "Customer Dashboard";

/**
 * Breadcrumb trail for an inner page: Overview > <section> > <detail>.
 * The last item has no `to` (current page). The second to last is the parent used for "Back to ...".
 */
export const getCustomerBreadcrumbs = (pathname) => {
  if (isCustomerRoot(pathname)) return [];
  const crumbs = [{ label: "Overview", to: CUSTOMER_PANEL_HOME }];
  const page = findPage(pathname);
  if (!page) return [...crumbs, { label: "Page" }];

  const rest = pathname.slice(page.prefix.length).split("/").filter(Boolean);
  if (rest.length === 0) {
    crumbs.push({ label: page.label });
  } else {
    crumbs.push({ label: page.label, to: page.to });
    const last = rest[rest.length - 1];
    crumbs.push({
      label: rest[0] === "new" ? "Create" : last === "edit" ? "Edit" : last === "discussion" ? "Discussion" : "Details",
    });
  }
  return crumbs;
};

const customerNavigation = {
  sections: customerSidebarSections,
};

export default customerNavigation;
